import {AxiosError} from "axios";
import {ApiError} from "types/global";
import {
    AUDIO_FILE_UPLOAD_EXTENSIONS,
    DOCUMENT_FILE_UPLOAD_EXTENSIONS,
    IMAGE_FILE_UPLOAD_EXTENSIONS,
    RESOURCE_FORMATS,
    VIDEO_FILE_UPLOAD_EXTENSIONS,
} from "../definitions/files";

export const createApiError = (e: AxiosError<ApiError>) => ({
    code: e?.response?.status ?? e?.code ?? 500,
    message: e?.response?.data?.message || e.message || 'Something goes wrong. Please try again',
    apiError: e,
});

const getExtensionFromMime = (mimeType: string) => {
    const map: Record<string, string> = {
        ...DOCUMENT_FILE_UPLOAD_EXTENSIONS, ...RESOURCE_FORMATS, ...IMAGE_FILE_UPLOAD_EXTENSIONS
    };

    return map[mimeType] || 'bin';
}

// The notification service serializes attachment types as MimeType enum
// names ("PNG", "PDF"), not MIME strings. Resolve either form to a MIME.
export const resolveAttachmentMimeType = (type: string) => {
    if (type.includes('/')) {
        return type;
    }

    const map: Record<string, string> = {
        ...IMAGE_FILE_UPLOAD_EXTENSIONS,
        ...DOCUMENT_FILE_UPLOAD_EXTENSIONS,
        ...VIDEO_FILE_UPLOAD_EXTENSIONS,
        ...AUDIO_FILE_UPLOAD_EXTENSIONS,
    };

    return map[type.toLowerCase()] ?? 'application/octet-stream';
};

export const formatKilobytes = (bytes: number) => `${Math.max(1, Math.round(bytes / 1024))} KB`;

export const base64ToFile = async (rawBase64: string, mimeType: string) => {
    const extension = getExtensionFromMime(mimeType);
    const base64WithPrefix = `data:${mimeType};base64,${rawBase64}`;

    const res = await fetch(base64WithPrefix);
    const blob = await res.blob();

    const fileName = `file.${extension}`;
    return new File([blob], fileName, {type: mimeType});
}

export const generateUrl = (
    routePath: string,
    params: Record<string, string | number>
) => {
    let url = routePath;

    Object.entries(params).forEach(([key, value]) => {
        url = url.replace(`:${key}`, String(value));
    });

    return url;
};

export const capitalizeFirstLetterAndLowerRest = (value: string | null) =>
    value ? value[0].toUpperCase() + value.slice(1).toLowerCase() : "";

export const isJwtExpired = (token: string) => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));

        return typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now();
    } catch {
        return false;
    }
};